<template>
	<div class="container py-4">
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/inventory/products"
				title="Back to your district's products"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div class="flex-grow-1">
				<h1 class="mb-0">{{ product?.name ?? "District Product" }}</h1>
				<p v-if="product?.catalog" class="text-muted mb-0">
					From the catalog:
					<span class="font-monospace">{{ product.catalog.name }}</span>
				</p>
			</div>
			<SaveIndicator
				:state="saveState"
				:pending="savePending"
				@retry="retrySave"
			/>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<template v-else-if="product">
			<!-- The district's own facts: the name above, and these three fields,
			     each of which saves on change with no button. -->
			<div class="card shadow-sm mb-4">
				<div class="card-body">
					<h2 class="h5 mb-3">Your district's settings</h2>
					<div class="row g-3">
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Control class</div>
							<select
								class="form-select"
								:value="product.control_class ?? ''"
								aria-label="Control class"
								@change="saveClass"
							>
								<option value="" disabled>Not set</option>
								<option
									v-for="option in CONTROL_CLASS_OPTIONS"
									:key="option.value"
									:value="option.value"
								>
									{{ option.label }}
								</option>
							</select>
							<div class="source-note">
								Drives each treatment's Method where this product is applied.
							</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Planned residual (days)</div>
							<input
								v-model="residualInput"
								type="number"
								min="0"
								step="1"
								class="form-control"
								:placeholder="
									product.residual_days_suggested !== null
										? String(product.residual_days_suggested)
										: undefined
								"
								aria-label="Planned residual days"
								@change="saveResidual"
							/>
							<div
								v-if="product.residual_days_suggested !== null"
								class="source-note"
							>
								The catalog suggests {{ product.residual_days_suggested }} days.
								Empty clears your planned residual.
							</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Target life stages</div>
							<LifeStageCircles
								:active="displayTargets"
								editable
								@toggle="toggleTarget"
							/>
							<div class="source-note">
								<template v-if="inheritingTargets">
									Showing the catalog's stages. Click a circle to set your own.
								</template>
								<template v-else>
									Click a stage to toggle which stages you use this product on.
								</template>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Read-only reference from the linked catalog entry. The district's
			     own name differs from the catalog's for some rows; both are shown. -->
			<div v-if="product.catalog" class="card shadow-sm mb-4">
				<div class="card-body">
					<div class="d-flex justify-content-between align-items-start mb-4">
						<div>
							<span class="badge rounded-pill bg-primary fs-5">{{
								product.catalog.category
							}}</span>
							<span
								v-if="nameDiffers"
								class="badge text-bg-warning ms-2 align-middle"
								title="The district's name for this product differs from the catalog's"
							>
								Name differs from catalog
							</span>
						</div>
						<div class="text-nowrap">
							<a
								v-if="product.catalog.label_document_url"
								class="btn btn-sm btn-primary me-1"
								:href="product.catalog.label_document_url"
								target="_blank"
								rel="noopener"
							>
								<i class="bi bi-file-earmark-pdf me-1"></i>Label
							</a>
							<a
								v-if="product.catalog.sds_document_url"
								class="btn btn-sm btn-outline-secondary"
								:href="product.catalog.sds_document_url"
								target="_blank"
								rel="noopener"
							>
								<i class="bi bi-file-earmark-pdf me-1"></i>SDS
							</a>
						</div>
					</div>

					<div class="row g-3">
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Catalog Name</div>
							<div>{{ product.catalog.name }}</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Formulation</div>
							<div>{{ product.catalog.formulation || "—" }}</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Manufacturer</div>
							<div>{{ product.catalog.manufacturer || "—" }}</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">EPA Registration</div>
							<div>{{ product.catalog.epa_registration || "—" }}</div>
						</div>
						<!-- The brand hop: the packaged product this row is. The
						     registration above is the legal parent; the brand is what
						     the technician picks up. -->
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Brand</div>
							<div>{{ product.catalog.registry_brand_name || "—" }}</div>
							<div
								v-if="
									product.catalog.registry_brand_id !== null &&
									product.catalog.registry_brand_sds_present === false
								"
								class="small text-danger mt-1"
							>
								<i class="bi bi-exclamation-triangle me-1"></i>No SDS filed for
								this brand
							</div>
							<a
								v-else-if="product.catalog.registry_brand_sds_document_url"
								class="small"
								:href="product.catalog.registry_brand_sds_document_url"
								target="_blank"
								rel="noopener"
							>
								<i class="bi bi-file-earmark-pdf me-1"></i>Brand SDS
							</a>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Active Ingredients</div>
							<div>{{ product.catalog.active_ingredients || "—" }}</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Catalog Target Life Stages</div>
							<LifeStageCircles :active="catalogTargets" />
						</div>
					</div>
				</div>
			</div>

			<div v-else class="alert alert-secondary" role="alert">
				This product is not linked to a catalog entry, so there are no reference
				facts for it.
			</div>

			<div v-if="product.catalog" class="card shadow-sm">
				<div class="card-body">
					<h2 class="h4 mb-3">Application Rates</h2>
					<div v-if="product.catalog.rates.length === 0" class="text-muted">
						No application rates recorded for this product.
					</div>
					<div v-else class="table-responsive">
						<table class="table table-striped align-middle">
							<thead class="table-light">
								<tr>
									<th>Scenario</th>
									<th>Target Life Stages</th>
									<th>Rate</th>
									<th>Residual (days)</th>
									<th>Notes</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="rate in product.catalog.rates" :key="rate.id">
									<td>{{ rate.scenario_description || "—" }}</td>
									<td>
										<LifeStageCircles
											:active="activeLifeStages(rate.target_life_stages)"
										/>
									</td>
									<td>
										<div>{{ formatRateRange([rate]) || "—" }}</div>
										<div
											v-if="formatPreferredRate(rate)"
											class="text-muted small"
										>
											Preferred: {{ formatPreferredRate(rate) }}
										</div>
									</td>
									<td>{{ formatResidualDays([rate]) || "—" }}</td>
									<td class="text-muted small">
										{{ rate.calculation_notes || "—" }}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import LifeStageCircles from "@/components/pesticide/LifeStageCircles.vue";
import SaveIndicator from "@/components/common/SaveIndicator.vue";
import { useSaveState } from "@/lib/autoSave";
import { CONTROL_CLASS_OPTIONS } from "@/lib/controlClass";
import { useStoreProduct } from "@/store/product";
import type { Product } from "@/type/api";
import {
	activeLifeStages,
	formatPreferredRate,
	formatRateRange,
	formatResidualDays,
	parseLifeStageCodes,
	serializeLifeStageCodes,
} from "@/view/configuration/pesticideCatalog";

const route = useRoute();
const storeProduct = useStoreProduct();
const {
	state: saveState,
	pending: savePending,
	run: runSave,
	retry: retrySave,
} = useSaveState("pesticide-detail");

const product = ref<Product | null>(null);
const loading = ref(false);
const error = ref("");
// residualInput mirrors the number box; the stored value is product.residual_days.
const residualInput = ref("");

const districtTargets = computed(() =>
	parseLifeStageCodes(product.value?.target_life_stages),
);
const catalogTargets = computed(() =>
	activeLifeStages(product.value?.catalog?.target_life_stages),
);
// A NULL target_life_stages means the district has not stated its own, so the
// circles show the catalog's stages — an inherit, not a blank.
const inheritingTargets = computed(
	() => (product.value?.target_life_stages ?? null) === null,
);
const displayTargets = computed(() =>
	inheritingTargets.value ? catalogTargets.value : districtTargets.value,
);
const nameDiffers = computed(
	() =>
		product.value?.catalog != null &&
		product.value.catalog.name.trim() !== product.value.name.trim(),
);

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const id = Number(route.params.id);
		product.value = await storeProduct.get(id);
		residualInput.value =
			product.value.residual_days !== null
				? String(product.value.residual_days)
				: "";
	} catch (e: any) {
		error.value = e?.message || "Failed to load this product.";
	} finally {
		loading.value = false;
	}
}

async function saveClass(event: Event) {
	if (!product.value) return;
	const value = (event.target as HTMLSelectElement).value;
	product.value.control_class = value;
	const id = product.value.id;
	await runSave(async () => {
		await storeProduct.save(id, { control_class: value });
	});
}

async function saveResidual() {
	if (!product.value) return;
	const raw = residualInput.value.trim();
	let days: number | null = null;
	if (raw !== "") {
		const parsed = Number(raw);
		if (!Number.isInteger(parsed) || parsed < 0) {
			return;
		}
		days = parsed;
	}
	product.value.residual_days = days;
	const id = product.value.id;
	await runSave(async () => {
		await storeProduct.save(id, { residual_days: days });
	});
}

async function toggleTarget(code: string) {
	if (!product.value) return;
	// Start from what is shown: an unset set inherits the catalog's stages, so
	// the first click commits that inherited state (with this toggle applied) as
	// the district's own.
	const next = new Set(displayTargets.value);
	if (next.has(code)) {
		next.delete(code);
	} else {
		next.add(code);
	}
	const serialized = serializeLifeStageCodes(next);
	product.value.target_life_stages = serialized === "" ? null : serialized;
	const id = product.value.id;
	await runSave(async () => {
		await storeProduct.save(id, { target_life_stages: serialized });
	});
}

onMounted(load);
</script>

<style scoped>
.info-label {
	font-size: 0.8rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #6c757d;
	margin-bottom: 0.25rem;
}

.source-note {
	font-size: 0.8rem;
	color: #6c757d;
	margin-top: 0.25rem;
}
</style>
